/**
 * Landing page (Reppr Elite, public site v5, Sep 2026).
 *
 * v5 is the "show, don't explain" pass, and it is shorter than v4 by half.
 *
 * WHO IT IS FOR. One reader: the agent or agency owner who pays. Athletes and
 * parents appear only as a reason to buy — the agent's edge — never as a
 * second audience with their own pitch.
 *
 * WHAT IT SAYS. Outcomes in the agent's own words, and a look at the real
 * screens. v4 published the mechanics: the comparables thresholds, the
 * spreadsheet-versus-Reppr table, a ranked sample of live signals, and the
 * fee arithmetic worked through. That is the product's thinking, and it was
 * free to read. It now lives in the walkthrough, where a person shows it on
 * the agency's own contract.
 *
 * HOW IT READS. Nothing on this page is smaller than 14px: the old site set
 * most of its body copy at 13px, which is a page written for the person who
 * built it rather than the person who buys it.
 *
 * Honesty rules carried forward, all deliberate:
 *  - every illustrative figure is invented and labelled Sample;
 *  - the parent's window prints an em dash where money would be, because that
 *    is what the portal really shows when earnings are off;
 *  - no customer names, no logos, no counts we cannot back.
 */

import { Link } from 'react-router-dom'
import PublicShell from '../components/public/PublicShell'
import { DeskMockup, PortalMockup } from '../components/public/Mockups'
import { useReveal } from '../hooks/useReveal'
import { DEMO_BOOKING_URL } from '../lib/config'
import shell from '../components/public/PublicShell.module.css'
import styles from './Landing.module.css'

/**
 * What an agent stops doing.
 *
 * Outcomes only. Where the contracts and seasons come from, and what reads
 * them, is the interesting part of the walkthrough and does not belong on a
 * page a competitor can read.
 */
const OUTCOMES = [
  {
    title: 'One place for the whole book',
    body:
      'Clients, deals, contracts, compliance and the money against each. Name an athlete and their file is waiting the next time you look.',
  },
  {
    title: 'You know what is coming, and what it is worth',
    body:
      'Every payment your athletes are due, dated, with your fee beside it. The spreadsheet you have been keeping alive can go.',
  },
  {
    title: 'Your clients stop calling to ask',
    body:
      'Each athlete gets a private statement, and a parent or advisor can have one too. They watch the money arrive instead of ringing you to ask where it is.',
  },
]

/** The same deal, as each party is allowed to see it. */
const WINDOWS = [
  {
    who: 'The agent',
    sub: 'Everything',
    rows: [{ k: 'Total value', v: '$12.5M' }, { k: 'Guaranteed', v: '$8.0M' }, { k: 'Your fee', v: '$375K' }],
  },
  {
    who: 'The athlete',
    sub: 'Their money',
    rows: [{ k: 'Total value', v: '$12.5M' }, { k: 'Guaranteed', v: '$8.0M' }, { k: 'Your fee', v: '—', muted: true }],
  },
  {
    who: 'A parent',
    sub: 'Invited by you',
    rows: [{ k: 'Total value', v: '—', muted: true }, { k: 'Guaranteed', v: '—', muted: true }, { k: 'Your fee', v: '—', muted: true }],
  },
]

/**
 * How an agency starts.
 *
 * The middle step is deliberately unexplained, and says what the agency does
 * NOT do rather than what we do. "We build the book" invited the only
 * question that matters to a sceptical agent: built from what? If the answer
 * sounds like their documents, the objection this page exists to remove walks
 * back in, because nobody is emailing a stranger their executed contracts.
 * "You never send us a document" is the claim, and how it is possible is the
 * strongest thing there is to show on a call. What the page must never do again is ask
 * for a roster spreadsheet or a folder of contract PDFs before someone has
 * seen a screen, which is what it used to open with.
 */
const STEPS = [
  { who: 'You', title: 'Sign up and name your athletes', body: 'A name and a league each. Nothing else leaves your side of the desk.', time: 'Minutes' },
  { who: 'Us', title: 'We do the rest', body: 'Contracts, seasons and the money against each, built while you sleep.', time: 'Overnight' },
  { who: 'You', title: 'Open the desk', body: 'Your first week of money is already laid out. Add a contract when you want the exact split and payout terms.', time: 'Next morning' },
]

export default function Landing() {
  const outcomesRef = useReveal<HTMLDivElement>()
  const portalRef = useReveal<HTMLDivElement>()
  const stepsRef = useReveal<HTMLDivElement>()
  const closeRef = useReveal<HTMLDivElement>()

  return (
    <PublicShell>
      {/* Hero: say what it is, then show it. */}
      <section className={shell.section}>
        <div className={`${shell.inner} ${styles.heroGrid}`}>
          <div className={styles.hero}>
            <span className={`${shell.eyebrow} ${styles.riseOne}`}>
              <span className={shell.eyebrowRule} />
              For sports agents, PR and talent managers
            </span>
            <h1 className={`${styles.heroTitle} ${styles.riseTwo}`}>Run the whole book from one desk.</h1>
            <p className={`${styles.heroBody} ${styles.riseThree}`}>
              Every client, contract and payment in one place, and a private statement for
              each athlete so your phone stops ringing about money.
            </p>
            {/*
              Pricing leads now that the numbers are published (24 Sep 2026).
              A solo agent can read the price, buy, and be working the same
              afternoon; making them book a call first was a toll on the people
              least likely to pay it. The walkthrough stays one click away for
              the agencies that want one, which is most of the larger ones.
            */}
            <div className={`${styles.ctaRow} ${styles.riseFour}`}>
              <Link to="/pricing" className={shell.btnPrimary}>Start from $79 a month</Link>
              <a href={DEMO_BOOKING_URL} target="_blank" rel="noopener noreferrer" className={shell.btnGhost}>
                Book a walkthrough
              </a>
            </div>
            <p className={`${styles.heroNote} ${styles.riseFour}`}>
              Every plan is the whole product. The walkthrough is forty-five minutes,
              screen shared, on one of your own contracts.
            </p>
          </div>

          <div className={`${styles.heroFig} ${styles.riseFive}`}>
            <DeskMockup />
          </div>
        </div>
      </section>

      {/* What changes, in three lines. */}
      <section id="product" className={shell.sectionAlt}>
        <div className={shell.inner}>
          <span className={shell.eyebrow}>What changes</span>
          <h2 className={styles.sectionTitle}>Three things stop being your job.</h2>
          <div ref={outcomesRef} className={styles.outcomes} data-reveal="out">
            {OUTCOMES.map((o, i) => (
              <article key={o.title} className={styles.outcome} style={{ '--i': i } as React.CSSProperties}>
                <h3 className={styles.outcomeTitle}>{o.title}</h3>
                <p className={styles.outcomeBody}>{o.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/*
        The portal, sold as the agent's edge, and the one thing shown in full.
        The claim is per-person visibility, which the product carries properly:
        client_users rows are typed athlete | parent | guardian | advisor and
        each holds its own show_contracts, show_incentives, show_earnings_detail
        and show_commission. A family with a business manager already involved
        is the case this wins.
      */}
      <section id="why" className={shell.section}>
        <div ref={portalRef} className={`${shell.inner} ${styles.portalGrid}`} data-reveal="out">
          <div className={styles.portalCopy}>
            <span className={shell.eyebrow}>The edge</span>
            <h2 className={styles.sectionTitle}>They sign with the agent who shows them.</h2>
            <p className={styles.sectionLede}>
              Everyone around your athlete can have their own login: the athlete, a parent
              or guardian, the financial advisor. You decide what each of them sees, down
              to whether they see your commission, and you can change it or take it back
              without touching anyone else's.
            </p>

            <div className={styles.windows}>
              <div className={styles.windowsHead}>
                <span className={styles.windowsLabel}>One deal · three windows</span>
                <span className={styles.windowsSample}>Sample</span>
              </div>
              <div className={styles.windowRow}>
                {WINDOWS.map((w) => (
                  <div key={w.who} className={styles.window}>
                    <div className={styles.windowWho}>{w.who}</div>
                    <div className={styles.windowSub}>{w.sub}</div>
                    <dl className={styles.windowRows}>
                      {w.rows.map((r) => (
                        <div key={r.k} className={styles.windowLine}>
                          <dt className={styles.windowKey}>{r.k}</dt>
                          <dd className={`${styles.windowVal} ${'muted' in r && r.muted ? styles.windowValHidden : ''}`}>{r.v}</dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                ))}
              </div>
              <p className={styles.windowsFoot}>
                A dash is a figure you chose not to share. A guardian or an advisor gets
                their own window on the same terms.
              </p>
            </div>
          </div>

          <div className={styles.portalFig}>
            <PortalMockup />
            <p className={styles.figCaption}>
              What the athlete opens on their phone.{' '}
              <Link to="/athletes" className={styles.figLink}>Send this to a family</Link>
            </p>
          </div>
        </div>
      </section>

      {/* Getting started. */}
      <section className={shell.sectionAlt}>
        <div className={shell.inner}>
          <span className={shell.eyebrow}>Getting started</span>
          <h2 className={styles.sectionTitle}>Three steps. One of them is ours.</h2>
          <div ref={stepsRef} className={styles.steps} data-reveal="out">
            {STEPS.map((s, i) => (
              <article key={s.title} className={styles.step} style={{ '--i': i } as React.CSSProperties}>
                <div className={styles.stepHead}>
                  <span className={styles.stepWho}>{s.who}</span>
                  <span className={styles.stepTime}>{s.time}</span>
                </div>
                <h3 className={styles.stepTitle}>{s.title}</h3>
                <p className={styles.stepBody}>{s.body}</p>
              </article>
            ))}
          </div>
          <p className={styles.stepsFoot}>
            You are never asked for a roster spreadsheet, an export, or a folder of
            contracts. How the book gets built is the part worth seeing, so we show it
            rather than write it down.
          </p>
        </div>
      </section>

      {/* Security, in one line — the detail is on request, not on the page. */}
      <section id="standards" className={shell.section}>
        <div className={shell.inner}>
          <span className={shell.eyebrow}>Handling</span>
          <h2 className={styles.sectionTitle}>Your contracts are nobody else's business.</h2>
          <p className={styles.sectionLede}>
            Encrypted in transit and kept apart from every other agency's. We never train
            a model on it, and you can export all of it whenever you like. Send your
            security questionnaire and we will fill it in.
          </p>
        </div>
      </section>

      {/* Close. */}
      <section className={shell.sectionAlt}>
        <div ref={closeRef} className={`${shell.inner} ${styles.close}`} data-reveal="out">
          <h2 className={styles.closeTitle}>Start today, or see it on your own contract.</h2>
          <p className={styles.closeBody}>
            One price per agency, every seat in your tier included, and signing a client
            never raises it. Or put one of your own contracts on the screen with us first
            and decide from there.
          </p>
          <div className={styles.ctaRow}>
            <Link to="/pricing" className={shell.btnPrimary}>See the prices</Link>
            <a href={DEMO_BOOKING_URL} target="_blank" rel="noopener noreferrer" className={shell.btnGhost}>
              Book a walkthrough
            </a>
          </div>
          <p className={styles.closeNote}>
            Music and marketing agencies run the same desk too. Ask on the call.
          </p>
        </div>
      </section>
    </PublicShell>
  )
}
